import React, { Dispatch, SetStateAction, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputZaverConfig } from "lib/types/generated/graphql-types"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"

import { ReactComponent as Logo } from "images/providers/zaver-logo.svg"
import { handleErrorMessages } from "helpers/errors"
import { GET_ZAVER_CONFIG } from "../../../../graphql/queries/config/Providers/Zaver/GetZaverConfig"
import { CREATE_OR_UPDATE_ZAVER_CONFIG } from "../../../../graphql/mutations/config/Providers/Zaver/CreateOrUpdateZaverConfig"
import { VALIDATE_ZAVER } from "../../../../graphql/queries/config/Providers/Zaver/ValidateZaverConfig"
import { DELETE_ZAVER_CONFIG } from "../../../../graphql/mutations/config/Providers/Zaver/DeleteZaverConfig"
import { ZaverConfigInputs } from "../../../../components/Config/Zaver/ZaverConfigInputs"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

interface ExtendedInputZaverConfig {
  id: string
  zaverConfig: InputZaverConfig
}

export const EditZaverConfig = ({ refetch, editId, setEditId }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const { data } = useQuery(GET_ZAVER_CONFIG, {
    variables: { id: editId }
  })
  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputZaverConfig>({ mode: "onBlur" })

  const [updateZaverConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_ZAVER_CONFIG,
    {
      onCompleted: () => {
        dispatch(hideEditSidebar())
        reset(zaverConfig)
        dispatch(
          alertActions.actions.createAlert({
            message: "zaver configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )
  const zaverConfig = data?.getZaverConfig
  const { id } = zaverConfig

  const [validateZaver, { loading }] = useLazyQuery(VALIDATE_ZAVER, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputZaverConfig> = (data) => {
    const { baseUrl, apiKey, callbackApiKey } = data.zaverConfig
    validateZaver({
      variables: {
        baseUrl,
        apiKey,
        callbackApiKey
      }
    }).then((response) => {
      if (response.data.Configuration.validate.zaver.valid) {
        updateZaverConfig({
          variables: {
            id: editId,
            zaverConfig: {
              baseUrl,
              apiKey,
              callbackApiKey
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  const directSubmit: SubmitHandler<ExtendedInputZaverConfig> = (data) => {
    const { baseUrl, apiKey, callbackApiKey } = data.zaverConfig

    updateZaverConfig({
      variables: {
        id: editId,
        zaverConfig: {
          baseUrl,
          apiKey,
          callbackApiKey
        }
      }
    })
    setValidationError(false)
  }

  if (!zaverConfig) return null

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar cancelEvent={() => closeSidebar()}>
        <EditSidebarHeader icon={<Logo />} title={id} id={id} cancelEvent={() => closeSidebar()}>
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <PrimaryButton type="submit" loading={createLoading || loading}>
              Save
            </PrimaryButton>
            <DeleteButton
              id={id}
              name={id}
              entity={"zaver configuration"}
              redirectUrl="/settings/providers/zaver"
              mutation={DELETE_ZAVER_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
          </WithAuth>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with zaver. Do you want to save the zaver config anyway?"
            buttonText="Yes, save"
            handleOkClick={handleSubmit(directSubmit)}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <ZaverConfigInputs
              control={control}
              errors={errors}
              setValue={setValue}
              defaultValues={zaverConfig}
              showId={false}
            />
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={zaverConfig.id}
              providerType={PROVIDER_TYPE.ZAVER}
              providerCategory={PROVIDER_CATEGORY.PAYMENT}
            />
          </TabPanel>
        </Tabs>
      </EditSidebar>
    </form>
  )
}
